'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type CountryCode = 'SD' | 'SA' | 'OM' | 'AE' | 'QA' | 'KW';

export interface Country {
  code: CountryCode;
  name: string;
  currency: string;
  prefix: string;
}

export const COUNTRIES: Country[] = [
  { code: 'SD', name: 'السودان', currency: 'ج.س', prefix: '249' },
  { code: 'SA', name: 'السعودية', currency: 'ر.س', prefix: '966' },
  { code: 'OM', name: 'عمان', currency: 'ر.ع', prefix: '968' },
  { code: 'AE', name: 'الإمارات', currency: 'د.إ', prefix: '971' },
  { code: 'QA', name: 'قطر', currency: 'ر.ق', prefix: '974' },
  { code: 'KW', name: 'الكويت', currency: 'د.ك', prefix: '965' },
];

interface CountryContextType {
  selectedCountryCode: CountryCode;
  setSelectedCountryCode: (code: CountryCode) => void;
  selectedCountry: Country | undefined;
}

const CountryContext = createContext<CountryContextType | undefined>(undefined);

export function CountryProvider({ children }: { children: ReactNode }) {
  const [selectedCountryCode, setSelectedCountryCode] = useState<CountryCode>('SD');

  useEffect(() => {
    const saved = localStorage.getItem('app_country_code') as CountryCode;
    if (saved && COUNTRIES.some(c => c.code === saved)) {
      setSelectedCountryCode(saved);
    }
  }, []);

  const handleSetCountry = (code: CountryCode) => {
    setSelectedCountryCode(code);
    localStorage.setItem('app_country_code', code);
  };

  const selectedCountry = COUNTRIES.find(c => c.code === selectedCountryCode);

  return (
    <CountryContext.Provider value={{ selectedCountryCode, setSelectedCountryCode: handleSetCountry, selectedCountry }}>
      {children}
    </CountryContext.Provider>
  );
}

export function useCountry() {
  const context = useContext(CountryContext);
  if (context === undefined) {
    throw new Error('useCountry must be used within a CountryProvider');
  }
  return context;
}
